import {
  Box,
  Autocomplete,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useEffect, useState } from "react";

import { PerspectiveViewFilter } from "../../entities/perspective_view_filter";
import { formatColumnCaption } from "../../utils/mui_table_utils";

const OPERATORS: { value: string; label: string }[] = [
  { value: "in", label: "is any of" },
  { value: "not in", label: "is none of" },
  { value: "contains", label: "contains" },
];

const toArray = (v: string[] | string | undefined): string[] =>
  Array.isArray(v) ? v : v != null && v !== "" ? [String(v)] : [];

const isSkuColumn = (name: string) => String(name).toLowerCase() === "sku";

/**
 * One applied filter, rendered as a self-contained vertically-stacked card:
 * a single-line header (field button / operator button / remove) over a
 * wrapping values area (removable chips + a "+ value" inline input). All edits
 * dispatch live through the callbacks — there is no confirm/cancel step.
 */
export const FilterComponent = ({
  filter,
  columns,
  isLoading,
  autoFocus,
  onAutoFocusConsumed,
  onChangeField,
  onChangeOperator,
  onAddValue,
  onAddValues,
  onRemoveValue,
  onRemove,
}: {
  filter: PerspectiveViewFilter;
  columns: { name: string; caption: string; unique_values: string[] }[];
  isLoading: boolean;
  autoFocus?: boolean;
  onAutoFocusConsumed?: () => void;
  onChangeField: (column: string) => void;
  onChangeOperator: (op: string) => void;
  onAddValue: (value: string) => void;
  onAddValues: (values: string[]) => void;
  onRemoveValue: (value: string) => void;
  onRemove: () => void;
}) => {
  const [fieldAnchor, setFieldAnchor] = useState<HTMLElement | null>(null);
  const [opAnchor, setOpAnchor] = useState<HTMLElement | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [skuOpen, setSkuOpen] = useState(false);
  const [skuText, setSkuText] = useState("");

  useEffect(() => {
    if (autoFocus) {
      setAdding(true);
      onAutoFocusConsumed?.();
    }
  }, [autoFocus, onAutoFocusConsumed]);

  const values = toArray(filter[2]);
  const fieldCol = columns.find((c) => c.name === filter[0]);
  const fieldCaption = fieldCol?.caption ?? formatColumnCaption(filter[0]);
  const opLabel =
    OPERATORS.find((o) => o.value === filter[1])?.label ?? String(filter[1]);

  const commitDraft = () => {
    const v = draft.trim();
    if (v) onAddValue(v);
    setDraft("");
  };

  const triggerSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    border: "none",
    background: "none",
    cursor: isLoading ? "default" : "pointer",
    padding: 0,
    minWidth: 0,
    fontFamily: "inherit",
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: (t) => t.palette.atomic.border,
        borderRadius: "8px",
        mb: "8px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: "6px",
          backgroundColor: (t) => t.palette.atomic.surfaceTint,
          borderBottom: "1px solid",
          borderColor: (t) => t.palette.atomic.borderLight,
          padding: "6px 6px 6px 9px",
        }}
      >
        <Box
          component="button"
          type="button"
          disabled={isLoading}
          onClick={(e) => setFieldAnchor(e.currentTarget)}
          sx={{
            ...triggerSx,
            maxWidth: 120,
            fontSize: "12.5px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.ink,
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fieldCaption}
          </Box>
          <ArrowDropDownIcon
            sx={{
              fontSize: 16,
              flexShrink: 0,
              color: (t) => t.palette.atomic.muted,
            }}
          />
        </Box>

        <Box
          component="button"
          type="button"
          disabled={isLoading}
          onClick={(e) => setOpAnchor(e.currentTarget)}
          sx={{
            ...triggerSx,
            fontSize: "12px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            color: (t) => t.palette.atomic.inkSecondary,
          }}
        >
          {opLabel}
          <ArrowDropDownIcon
            sx={{
              fontSize: 16,
              flexShrink: 0,
              color: (t) => t.palette.atomic.muted,
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <IconButton
          onClick={onRemove}
          disabled={isLoading}
          sx={{ width: 22, height: 22, padding: 0, flexShrink: 0 }}
        >
          <CloseIcon
            sx={{ fontSize: 16, color: (t) => t.palette.atomic.muted }}
          />
        </IconButton>
      </Box>

      <Menu
        anchorEl={fieldAnchor}
        open={Boolean(fieldAnchor)}
        onClose={() => setFieldAnchor(null)}
      >
        {[...columns]
          .sort((a, b) =>
            (a.caption || a.name).localeCompare(b.caption || b.name)
          )
          .map((c) => (
          <MenuItem
            key={c.name}
            selected={c.name === filter[0]}
            onClick={() => {
              onChangeField(c.name);
              setFieldAnchor(null);
            }}
            sx={{ fontSize: 13 }}
          >
            {c.caption ?? formatColumnCaption(c.name)}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={opAnchor}
        open={Boolean(opAnchor)}
        onClose={() => setOpAnchor(null)}
      >
        {OPERATORS.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === filter[1]}
            onClick={() => {
              onChangeOperator(o.value);
              setOpAnchor(null);
            }}
            sx={{ fontSize: 13 }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "5px",
          padding: "8px 9px",
          alignItems: "center",
        }}
      >
        {values.length === 0 && !adding && (
          <Box
            sx={{
              fontStyle: "italic",
              fontSize: 12,
              color: (t) => t.palette.atomic.muted,
            }}
          >
            no values
          </Box>
        )}

        {values.map((v) => (
          <Box
            key={v}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              maxWidth: "100%",
              backgroundColor: (t) => t.palette.atomic.blue1,
              color: (t) => t.palette.atomic.brandBlueDark,
              border: "1px solid #c8d8f5",
              borderRadius: "10px",
              padding: "2px 4px 2px 8px",
              fontSize: "11.5px",
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 130,
              }}
            >
              {v}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => onRemoveValue(v)}
              sx={{ ...triggerSx, color: "inherit", lineHeight: 1 }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </Box>
          </Box>
        ))}

        {adding ? (
          <Autocomplete
            freeSolo
            autoHighlight
            openOnFocus
            size="small"
            // Coerce defensively: unique_values can carry non-strings (raw
            // booleans, or nulls from a stale schema) and toLowerCase below
            // must never throw mid-render.
            options={(fieldCol?.unique_values ?? [])
              .filter((o) => o != null)
              .map((o) => String(o))
              .filter((o) => !values.includes(o))}
            inputValue={draft}
            onInputChange={(_e, val, reason) => {
              if (reason === "input" || reason === "clear") setDraft(val);
            }}
            onChange={(_e, val) => {
              const picked = typeof val === "string" ? val : val ?? "";
              const trimmed = picked.trim();
              if (trimmed) onAddValue(trimmed);
              setDraft("");
            }}
            filterOptions={(opts, state) => {
              const input = state.inputValue.toLowerCase().trim();
              const matched = input
                ? opts.filter((o) => o.toLowerCase().includes(input))
                : opts;
              // Cap suggestions so high-cardinality columns (e.g. SKU) stay snappy.
              return matched.slice(0, 100);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDraft("");
                setAdding(false);
              } else if (
                e.key === "Backspace" &&
                draft === "" &&
                values.length > 0
              ) {
                onRemoveValue(values[values.length - 1]);
              }
            }}
            onBlur={() => {
              commitDraft();
              setAdding(false);
            }}
            renderOption={(props: any, option: string) => {
              const { key, ...optionProps } = props;
              return (
                <Box component="li" key={key} {...optionProps} sx={{ fontSize: 13 }}>
                  {option}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                variant="standard"
                placeholder="search…"
                sx={{
                  width: 140,
                  "& input": { fontSize: 12, padding: "2px 0" },
                }}
              />
            )}
            sx={{ display: "inline-flex" }}
          />
        ) : (
          <Box
            component="button"
            type="button"
            disabled={isLoading}
            onClick={() => setAdding(true)}
            sx={{
              border: "1px dashed",
              borderColor: (t) => t.palette.atomic.faint,
              background: "none",
              cursor: "pointer",
              borderRadius: "10px",
              padding: "2px 8px",
              fontSize: "11.5px",
              fontFamily: "inherit",
              color: (t) => t.palette.atomic.muted,
            }}
          >
            + value
          </Box>
        )}
      </Box>

      {isSkuColumn(filter[0]) && (
        <Box sx={{ px: "9px", pb: "8px" }}>
          <Button
            fullWidth
            onClick={() => setSkuOpen(true)}
            sx={{
              justifyContent: "center",
              whiteSpace: "nowrap",
              fontSize: "12px",
              color: (t) => t.palette.atomic.inkSecondary,
              border: "1px solid",
              borderColor: (t) => t.palette.atomic.border,
              borderRadius: "6px",
              padding: "4px 8px",
            }}
          >
            Paste SKU list
          </Button>
        </Box>
      )}

      <Dialog
        open={skuOpen}
        onClose={() => setSkuOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16 }}>Paste SKU list</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={5}
            fullWidth
            placeholder="One SKU per line, or comma-separated"
            value={skuText}
            onChange={(e) => setSkuText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSkuOpen(false);
              setSkuText("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const parsed = skuText
                .split(/[\n,]/)
                .map((s) => s.trim())
                .filter(Boolean);
              const unique = Array.from(new Set(parsed));
              if (unique.length) onAddValues(unique);
              setSkuOpen(false);
              setSkuText("");
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
